import { useEffect, useMemo, useRef } from "react";

import { faChartSimple, faThumbtack } from "@fortawesome/pro-regular-svg-icons";
import { useVegaEmbed } from "react-vega";
import { type TopLevelSpec } from "vega-lite";

import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { EmptyState } from "src/design-system/EmptyState";
import { type MetricsQueryRow } from "src/flow/analytics/m1/useMetricsQuery";

const VEGA_SPEC_DISCRIMINATORS = [
  "mark",
  "layer",
  "facet",
  "repeat",
  "concat",
  "vconcat",
  "hconcat",
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isVegaLiteSpec = (chartDefinition: Record<string, unknown>): boolean =>
  VEGA_SPEC_DISCRIMINATORS.some((key) => key in chartDefinition);

const extractTitle = (chartDefinition: Record<string, unknown>): string => {
  const { title } = chartDefinition;
  if (typeof title === "string") return title;
  if (isRecord(title) && typeof title.text === "string") return title.text;
  return "";
};

const buildSpec = (
  chartDefinition: Record<string, unknown>,
  rows: MetricsQueryRow[],
): TopLevelSpec =>
  ({
    ...chartDefinition,
    width: "container",
    height: "container",
    autosize: { contains: "padding" },
    title: null,
    config: isRecord(chartDefinition.config) ? chartDefinition.config : {},
    data: { values: rows },
  }) as unknown as TopLevelSpec;

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Card variant="inverted" className="flex h-full flex-col">
    <Card.Header>
      <Card.Title title={title} />
      <Button iconLeft={faThumbtack} size="sm" variant="primary" disabled>
        Pin
      </Button>
    </Card.Header>
    <Card.Content className="min-h-0 flex-1">{children}</Card.Content>
  </Card>
);

const VegaChart: React.FC<{
  rows: MetricsQueryRow[];
  chartDefinition: Record<string, unknown>;
}> = ({ rows, chartDefinition }) => {
  const embedRef = useRef<HTMLDivElement>(null);

  const spec = useMemo(
    () => buildSpec(chartDefinition, rows),
    [chartDefinition, rows],
  );

  const embed = useVegaEmbed({
    ref: embedRef,
    options: { actions: false, mode: "vega-lite" },
    spec,
  });

  useEffect(() => {
    const container = embedRef.current;
    if (!container || !embed) return;
    const observer = new ResizeObserver(() => {
      window.dispatchEvent(new Event("resize"));
      embed.view.runAsync();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [embed]);

  return <div ref={embedRef} className="h-full w-full" />;
};

export const MetricsChart: React.FC<{
  rows: MetricsQueryRow[];
  chartDefinition: Record<string, unknown>;
}> = ({ rows, chartDefinition }) => {
  if (!isVegaLiteSpec(chartDefinition)) {
    return (
      <Card variant="inverted" className="flex h-full flex-col">
        <EmptyState
          description="The chart definition is missing a mark or layer."
          headline="Invalid chart definition"
          icon={faChartSimple}
          size="compact"
          variant="error"
        />
      </Card>
    );
  }

  return (
    <ChartCard title={extractTitle(chartDefinition)}>
      <VegaChart chartDefinition={chartDefinition} rows={rows} />
    </ChartCard>
  );
};
